<script setup>
import { ref, computed } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useToggle } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useEmitter } from 'dashboard/composables/emitter';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useConversationRequiredAttributes } from 'dashboard/composables/useConversationRequiredAttributes';
import { useAdmin } from 'dashboard/composables/useAdmin';

import WootDropdownItem from 'shared/components/ui/dropdown/DropdownItem.vue';
import WootDropdownMenu from 'shared/components/ui/dropdown/DropdownMenu.vue';
import wootConstants from 'dashboard/constants/globals';
import {
  CMD_REOPEN_CONVERSATION,
  CMD_RESOLVE_CONVERSATION,
} from 'dashboard/helper/commandbar/events';

import ButtonGroup from 'dashboard/components-next/buttonGroup/ButtonGroup.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ConversationResolveAttributesModal from 'dashboard/components-next/ConversationWorkflow/ConversationResolveAttributesModal.vue';

const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();
const { checkMissingAttributes } = useConversationRequiredAttributes();
const { isAdmin } = useAdmin();

const arrowDownButtonRef = ref(null);
const isLoading = ref(false);
const resolveAttributesModalRef = ref(null);

const [showActionsDropdown, toggleDropdown] = useToggle();
const closeDropdown = () => toggleDropdown(false);
const openDropdown = () => toggleDropdown(true);

const currentChat = computed(() => getters.getSelectedChat.value);

const isOpen = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.OPEN
);
const isPending = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.PENDING
);
const isResolved = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.RESOLVED
);
const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);

const showAdditionalActions = computed(
  () => !isPending.value && !isSnoozed.value
);
const showPendingAction = computed(
  () => isAdmin.value && showAdditionalActions.value
);

const showOpenButton = computed(() => {
  return isPending.value || isSnoozed.value;
});

const getConversationParams = () => {
  const allConversations = document.querySelectorAll(
    '.conversations-list .conversation'
  );

  const activeConversation = document.querySelector(
    'div.conversations-list div.conversation.active'
  );
  const activeConversationIndex = [...allConversations].indexOf(
    activeConversation
  );
  const lastConversationIndex = allConversations.length - 1;

  return {
    all: allConversations,
    activeIndex: activeConversationIndex,
    lastIndex: lastConversationIndex,
  };
};

const openSnoozeModal = () => {
  closeDropdown();
  const ninja = document.querySelector('ninja-keys');
  ninja.open({ parent: 'snooze_conversation' });
};

const toggleStatus = (status, snoozedUntil, customAttributes = null) => {
  closeDropdown();
  isLoading.value = true;

  const payload = {
    conversationId: currentChat.value.id,
    status,
    snoozedUntil,
  };

  if (customAttributes) {
    payload.customAttributes = customAttributes;
  }

  store.dispatch('toggleStatus', payload).then(() => {
    useAlert(t('CONVERSATION.CHANGE_STATUS'));
    isLoading.value = false;
  });
};

const handleResolveWithAttributes = ({ attributes, context }) => {
  if (context) {
    const currentCustomAttributes = currentChat.value.custom_attributes || {};
    const mergedAttributes = { ...currentCustomAttributes, ...attributes };
    toggleStatus(
      wootConstants.STATUS_TYPE.RESOLVED,
      context.snoozedUntil,
      mergedAttributes
    );
  }
};

const onCmdOpenConversation = () => {
  toggleStatus(wootConstants.STATUS_TYPE.OPEN);
};

const onCmdResolveConversation = () => {
  const currentCustomAttributes = currentChat.value.custom_attributes || {};
  const { hasMissing, missing } = checkMissingAttributes(
    currentCustomAttributes
  );

  if (hasMissing) {
    const conversationContext = {
      id: currentChat.value.id,
      snoozedUntil: null,
    };
    resolveAttributesModalRef.value?.open(
      missing,
      currentCustomAttributes,
      conversationContext
    );
  } else {
    toggleStatus(wootConstants.STATUS_TYPE.RESOLVED);
  }
};

const keyboardEvents = {
  'Alt+KeyM': {
    action: () => arrowDownButtonRef.value?.$el.click(),
    allowOnFocusedInput: true,
  },
  'Alt+KeyE': {
    action: async event => {
      // Chrome on Windows treats Alt+E as a legacy shortcut for its own
      // menu (Alt+E / Alt+F both open "Customize and control Google
      // Chrome"). Without preventDefault, our resolve action still runs,
      // but Chrome's menu also opens on top of it.
      event.preventDefault();
      onCmdResolveConversation();
    },
  },
  '$mod+Alt+KeyE': {
    action: async event => {
      const { all, activeIndex, lastIndex } = getConversationParams();
      onCmdResolveConversation();

      if (activeIndex < lastIndex) {
        all[activeIndex + 1].click();
      } else if (all.length > 1) {
        all[0].click();
        document.querySelector('.conversations-list').scrollTop = 0;
      }
      event.preventDefault();
    },
  },
};

useKeyboardEvents(keyboardEvents);

useEmitter(CMD_REOPEN_CONVERSATION, onCmdOpenConversation);
useEmitter(CMD_RESOLVE_CONVERSATION, onCmdResolveConversation);
</script>

<template>
  <div class="flex justify-end items-center gap-2 resolve-actions">
    <Button
      v-if="showAdditionalActions"
      :label="t('CONVERSATION.RESOLVE_DROPDOWN.SNOOZE_UNTIL')"
      icon="i-lucide-alarm-clock-minus"
      size="sm"
      color="slate"
      no-animation
      class="shrink-0 shadow"
      :disabled="isLoading"
      @click="openSnoozeModal"
    />
    <div class="relative">
      <ButtonGroup
        class="flex-shrink-0 rounded-lg shadow outline-1 outline"
        :class="!showOpenButton ? 'outline-n-container' : 'outline-transparent'"
      >
        <Button
          v-if="isOpen"
          :label="t('CONVERSATION.HEADER.RESOLVE_ACTION')"
          icon="i-lucide-badge-check"
          size="sm"
          color="slate"
          no-animation
          class="ltr:rounded-r-none rtl:rounded-l-none !outline-0"
          :is-loading="isLoading"
          @click="onCmdResolveConversation"
        />
        <Button
          v-else-if="isResolved"
          :label="t('CONVERSATION.HEADER.REOPEN_ACTION')"
          size="sm"
          color="slate"
          no-animation
          class="ltr:rounded-r-none rtl:rounded-l-none !outline-0"
          :is-loading="isLoading"
          @click="onCmdOpenConversation"
        />
        <Button
          v-else-if="showOpenButton"
          :label="t('CONVERSATION.HEADER.OPEN_ACTION')"
          size="sm"
          color="slate"
          no-animation
          :is-loading="isLoading"
          @click="onCmdOpenConversation"
        />
        <Button
          v-if="showAdditionalActions"
          ref="arrowDownButtonRef"
          icon="i-lucide-chevron-down"
          :disabled="isLoading || !showPendingAction"
          :aria-label="t('CONVERSATION.HEADER.MORE_ACTIONS')"
          :aria-expanded="showActionsDropdown && showPendingAction"
          size="sm"
          no-animation
          class="ltr:rounded-l-none rtl:rounded-r-none !outline-0"
          color="slate"
          trailing-icon
          @click="openDropdown"
        />
      </ButtonGroup>
      <div
        v-if="showActionsDropdown && showPendingAction"
        v-on-clickaway="closeDropdown"
        class="border rounded-lg shadow-lg border-n-strong dark:border-n-strong box-content p-2 w-fit z-10 bg-n-alpha-3 backdrop-blur-[100px] absolute block left-auto top-full mt-0.5 start-0 xl:start-auto xl:end-0 max-w-[12.5rem] min-w-[9.75rem] [&_ul>li]:mb-0"
      >
        <WootDropdownMenu class="mb-0">
          <WootDropdownItem>
            <Button
              :label="t('CONVERSATION.RESOLVE_DROPDOWN.MARK_PENDING')"
              ghost
              slate
              sm
              start
              icon="i-lucide-circle-dot-dashed"
              class="w-full"
              @click="() => toggleStatus(wootConstants.STATUS_TYPE.PENDING)"
            />
          </WootDropdownItem>
        </WootDropdownMenu>
      </div>
    </div>
    <ConversationResolveAttributesModal
      ref="resolveAttributesModalRef"
      @submit="handleResolveWithAttributes"
    />
  </div>
</template>
